import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getPmsThemeDefinition,
  isKnownPmsThemeId,
  PMS_DEFAULT_THEME_ID,
  PMS_THEME_OPTIONS,
  PMS_THEME_STORAGE_KEY,
  readPmsThemeIdFromUser,
  readStoredPmsThemeId,
  storePmsThemeId,
} from "../utils/pmsThemes";
import {
  getPmsLanguageDefinition,
  isKnownPmsLanguageId,
  PMS_DEFAULT_LANGUAGE_ID,
  PMS_LANGUAGE_OPTIONS,
  PMS_LANGUAGE_STORAGE_KEY,
  readStoredPmsLanguageId,
  storePmsLanguageId,
} from "../utils/pmsLanguages";
import { isDemoMode, readDemoLanguageId, readDemoUser } from "../demo/demoMode";
import { persistCurrentUserPreferences, readStoredUser } from "../utils/sessionUser";

const PmsThemeContext = createContext(null);

function readSessionThemeId() {
  // The provider mounts before the asynchronous demo entry creates its user.
  // Do not consult real-user caches or run their local-storage migration then.
  if (isDemoMode()) return readPmsThemeIdFromUser(readDemoUser()) || PMS_DEFAULT_THEME_ID;
  return readStoredPmsThemeId(readStoredUser());
}

function readSessionLanguageId() {
  return isDemoMode() ? readDemoLanguageId() : readStoredPmsLanguageId(readStoredUser());
}

export function PmsThemeProvider({ children }) {
  const [themeId, setThemeIdState] = useState(readSessionThemeId);
  const [languageId, setLanguageIdState] = useState(readSessionLanguageId);

  useEffect(() => {
    const normalizedThemeId = isKnownPmsThemeId(themeId) ? themeId : PMS_DEFAULT_THEME_ID;
    const safeThemeId = isDemoMode() ? normalizedThemeId : storePmsThemeId(normalizedThemeId, readStoredUser());
    const theme = getPmsThemeDefinition(safeThemeId);

    if (safeThemeId !== themeId) {
      setThemeIdState(safeThemeId);
      return;
    }

    document.documentElement.dataset.pmsTheme = safeThemeId;
    document.documentElement.style.colorScheme = theme.colorScheme || "dark";
    window.dispatchEvent(
      new CustomEvent("palantir-pms-theme-updated", { detail: { themeId: safeThemeId } })
    );
  }, [themeId]);

  useEffect(() => {
    const normalizedLanguageId = isKnownPmsLanguageId(languageId) ? languageId : PMS_DEFAULT_LANGUAGE_ID;
    const safeLanguageId = isDemoMode() ? normalizedLanguageId : storePmsLanguageId(normalizedLanguageId, readStoredUser());
    const language = getPmsLanguageDefinition(safeLanguageId);

    if (safeLanguageId !== languageId) {
      setLanguageIdState(safeLanguageId);
      return;
    }

    document.documentElement.lang = safeLanguageId;
    window.dispatchEvent(
      new CustomEvent("palantir-pms-language-updated", {
        detail: { languageId: safeLanguageId, locale: language.locale },
      })
    );
  }, [languageId]);

  useEffect(() => {
    const syncFromStoredSession = () => {
      const nextThemeId = readSessionThemeId();
      const nextLanguageId = readSessionLanguageId();

      setThemeIdState((prev) => (prev === nextThemeId ? prev : nextThemeId));
      setLanguageIdState((prev) => (prev === nextLanguageId ? prev : nextLanguageId));
    };

    window.addEventListener("palantir-session-updated", syncFromStoredSession);
    return () => {
      window.removeEventListener("palantir-session-updated", syncFromStoredSession);
    };
  }, []);

  const setThemeId = useCallback((nextThemeId) => {
    if (isDemoMode()) return;
    const safeThemeId = isKnownPmsThemeId(nextThemeId) ? nextThemeId : PMS_DEFAULT_THEME_ID;
    const safeLanguageId = isKnownPmsLanguageId(languageId)
      ? languageId
      : PMS_DEFAULT_LANGUAGE_ID;

    setThemeIdState(safeThemeId);
    void persistCurrentUserPreferences({
      pms_theme_id: safeThemeId,
      pms_language_id: safeLanguageId,
    });
  }, [languageId]);

  const setLanguageId = useCallback((nextLanguageId) => {
    if (isDemoMode()) return;
    const safeLanguageId = isKnownPmsLanguageId(nextLanguageId)
      ? nextLanguageId
      : PMS_DEFAULT_LANGUAGE_ID;
    const safeThemeId = isKnownPmsThemeId(themeId) ? themeId : PMS_DEFAULT_THEME_ID;

    setLanguageIdState(safeLanguageId);
    void persistCurrentUserPreferences({
      pms_theme_id: safeThemeId,
      pms_language_id: safeLanguageId,
    });
  }, [themeId]);

  const value = useMemo(
    () => ({
      themeId,
      theme: getPmsThemeDefinition(themeId),
      themes: PMS_THEME_OPTIONS,
      languageId,
      language: getPmsLanguageDefinition(languageId),
      languages: PMS_LANGUAGE_OPTIONS,
      setThemeId,
      setLanguageId,
      storageKey: PMS_THEME_STORAGE_KEY,
      languageStorageKey: PMS_LANGUAGE_STORAGE_KEY,
    }),
    [languageId, setLanguageId, setThemeId, themeId]
  );

  return <PmsThemeContext.Provider value={value}>{children}</PmsThemeContext.Provider>;
}

export function usePmsTheme() {
  const context = useContext(PmsThemeContext);

  if (!context) {
    throw new Error("usePmsTheme must be used within a PmsThemeProvider");
  }

  return context;
}
